import React, { useState, useMemo, useEffect, useRef } from 'react';
import { startOfMinute, endOfMinute, addMonths, subMonths, min, subDays } from 'date-fns';
import dateFormat from 'dateformat';
// TODO: https://github.com/mui/mui-x/issues/4644
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSelector } from 'react-redux';
import { func, instanceOf, oneOfType, shape, string } from 'prop-types';
import { DateRangeRoot, pickerClassName } from './styles';
import { getDateTimeFormat, isInMonthRange } from '#helper';

/**
 * Renders start and end keyboard date pickers.
 * @returns {React.FC}
 */

const DateRange = ({
  isInRange,
  totalMonthRange,
  getThreeMonthIntervalFn,
  rangeStart = null,
  rangeEnd = null,
  setDateRange = () => {},
  minDate = undefined,
  maxDate = undefined,
  classes,
  openEndDateDialog,
  setOpenEndDateDialog,
  ...restProps
}) => {
  const m = `month${totalMonthRange > 1 ? 's' : ''}`;
  let initError = rangeStart > rangeEnd ? '"end" must be greater than "start"' : null;
  initError = isInRange ? initError : `Must be in ${totalMonthRange} ${m} range`;

  const measurementSystem = useSelector(s => s.account.measurementSystem);
  const [formattedRange, setFormattedRange] = useState({
    start: new Date(rangeStart),
    end: new Date(rangeEnd)
  });
  const isEditingRef = useRef(false);
  const [startError, setStartError] = useState(initError);
  const [endError, setEndError] = useState(initError);

  // Sync local state when props change externally (e.g., navigation buttons)
  useEffect(() => {
    if (!isEditingRef.current) {
      setFormattedRange({
        start: new Date(rangeStart),
        end: new Date(rangeEnd)
      });
      setStartError(null);
      setEndError(null);
    }
  }, [rangeStart, rangeEnd]);

  const formatFns = useMemo(() => getDateTimeFormat(measurementSystem, 'dateTimeShortDateFns'), [measurementSystem]);
  const dateTimeFormat = useMemo(() => getDateTimeFormat(measurementSystem, 'dateTimeShort'), [measurementSystem]);

  const getInterval = direction => {
    const isStart = direction === 'start';
    const rangeDate = new Date(isStart ? rangeEnd : rangeStart);
    const getMonthFn = isStart ? subMonths : addMonths;
    const roundTime = isStart ? startOfMinute : endOfMinute;
    return roundTime(getMonthFn(rangeDate, totalMonthRange)).toISOString();
  };

  const handleStartChange = date => {
    isEditingRef.current = false;
    setStartError(null);
    setEndError(null);

    const currentEndDate = new Date(rangeEnd);
    const adjustedEndDate = min([
      currentEndDate,
      subDays(addMonths(new Date(date), totalMonthRange), 1),
      new Date()
    ]);
    const interval = getThreeMonthIntervalFn ? getThreeMonthIntervalFn('start', rangeStart, adjustedEndDate) : getInterval('start');
    const isValidRange = isInMonthRange(date, adjustedEndDate, totalMonthRange);

    // second check means that date should be greater than minimal possible in JS Date object
    if (Date.parse(date) && date > new Date(0)) {
      if (minDate && date < new Date(minDate)) {
        setStartError(`Must be later than project start date: ${dateFormat(minDate, dateTimeFormat)}`);
      } else if (date > currentEndDate) {
        setStartError(`Must be earlier than ${dateFormat(currentEndDate, dateTimeFormat)}`);
      } else if (date.toISOString() < interval && !isValidRange) {
        const month = `month${totalMonthRange > 1 ? 's' : ''}`;
        setStartError(`Must be later than: ${dateFormat(interval, dateTimeFormat)} (${totalMonthRange}${String.fromCharCode(160)}${month} before end date)`);
      } else {
        setDateRange(date, adjustedEndDate);
        setOpenEndDateDialog(true);
      }
    } else {
      setStartError('Invalid date');
    }
  };

  const handleEndChange = date => {
    isEditingRef.current = false;
    setStartError(null);
    setEndError(null);

    const currentStartDate = new Date(rangeStart);
    const currentMaxDate = new Date(maxDate);
    const interval = getThreeMonthIntervalFn ? getThreeMonthIntervalFn('end', rangeStart, rangeEnd) : getInterval('end');
    const isValidRange = isInMonthRange(rangeStart, date, totalMonthRange);

    // second check means that date should be greater than minimal possible in JS Date object
    if (Date.parse(date) && date > new Date(0)) {
      if (maxDate && date > currentMaxDate) {
        setEndError(`Must be earlier than max end date: ${dateFormat(maxDate, dateTimeFormat)}`);
      } else if (date < currentStartDate) {
        setEndError(`Must be later than: ${dateFormat(rangeStart, dateTimeFormat)}`);
      } else if (date.toISOString() > interval && !isValidRange) {
        const month = `month${totalMonthRange > 1 ? 's' : ''}`;
        setEndError(`Must be earlier than: ${dateFormat(interval, dateTimeFormat)} (${totalMonthRange}${String.fromCharCode(160)}${month} after start date)`);
      } else {
        setDateRange(rangeStart, date);
        setOpenEndDateDialog(false);
      }
    } else {
      setEndError('Invalid date');
    }
  };

  const handleFormattedRange = key => date => {
    isEditingRef.current = true;
    setFormattedRange({ ...formattedRange, [key]: date });
  };
  const handleFormattedStartRange = handleFormattedRange('start');
  const handleFormattedEndRange = handleFormattedRange('end');

  const dateProps = {
    format: formatFns,
    inputProps: {
      className: pickerClassName.fontSize ? undefined : undefined, style: pickerClassName
    },
    inputVariant: 'outlined'
  };

  useEffect(() => {
    const closeEndDateDialog = e => {
      const pickerHeaderEl = e.target.closest('.MuiPickersCalendarHeader-root');
      if (!pickerHeaderEl) {
        setOpenEndDateDialog(false);
      }
    };
    document.body.addEventListener('click', closeEndDateDialog);

    return () => {
      document.body.removeEventListener('click', closeEndDateDialog);
    };
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateRangeRoot {...restProps}>
        <DesktopDatePicker
          label="Start"
          value={formattedRange.start}
          onChange={handleFormattedStartRange}
          onAccept={handleStartChange}
          minDate={new Date(minDate)}
          maxDate={new Date(rangeEnd)}
          strictCompareDates
          slotProps={{
            textField: {
              error: !!startError,
              helperText: startError,
              onKeyDown: async event => {
                if (event.key === 'Enter') {
                  event.target.blur();
                }
              },
              onBlur: () => {
                if (isEditingRef.current) {
                  handleStartChange(formattedRange.start);
                }
              }
            },
            openPickerIcon: {
              onClick: e => {
                e.nativeEvent.stopImmediatePropagation();
                setOpenEndDateDialog(false);
              }
            }
          }}
          {...dateProps}
        />
        <DesktopDatePicker
          label="End"
          value={formattedRange.end}
          onChange={handleFormattedEndRange}
          onAccept={handleEndChange}
          minDate={new Date(rangeStart)}
          maxDate={new Date(maxDate)}
          strictCompareDates
          slotProps={{
            textField: {
              error: !!endError,
              helperText: endError,
              onKeyDown: async event => {
                if (event.key === 'Enter') {
                  event.target.blur();
                }
              },
              onBlur: () => {
                if (isEditingRef.current) {
                  handleEndChange(formattedRange.end);
                }
              }
            },
            openPickerIcon: {
              onClick: e => {
                e.nativeEvent.stopImmediatePropagation();
                setOpenEndDateDialog(!openEndDateDialog);
              }
            }
          }}
          open={openEndDateDialog}
          {...dateProps}
        />
      </DateRangeRoot>
    </LocalizationProvider>
  );
};

DateRange.propTypes = {
  rangeStart: oneOfType([instanceOf(Date), string]),
  rangeEnd: oneOfType([instanceOf(Date), string]),
  maxDate: oneOfType([instanceOf(Date), string]),
  minDate: oneOfType([instanceOf(Date), string]),
  setDateRange: func,
  classes: shape({})

};

export default React.memo(DateRange);
